import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import type {AuctionBid, CarAuction} from "@/types";
import type {RootState} from "@/store";
import {keysToCamelCase, normalizeError, supabase} from "@/utils";
import {toCarAuction, toNumber} from "@/utils/dbHelpers.ts";

interface AuctionState {
    auctions: CarAuction[];
    endingSoon: CarAuction[];
    newlyListed: CarAuction[];
    noReserve: CarAuction[];
    lowestMileage: CarAuction[];
    inspected: CarAuction[];
    relatedEndingSoon: CarAuction[];
    relatedNewlyListed: CarAuction[];
    relatedReferenceId: string | null;
    currentAuction: CarAuction | null;
    loading: boolean;
    currentAuctionLoading: boolean;
    placingBid: boolean;
    error: string | null;
    topBidderLoading: boolean;
    auctionsFetched: boolean;
}

const initialState: AuctionState = {
    auctionsFetched: false,
    auctions: [],
    endingSoon: [],
    newlyListed: [],
    noReserve: [],
    lowestMileage: [],
    inspected: [],
    relatedEndingSoon: [],
    relatedNewlyListed: [],
    relatedReferenceId: null,
    currentAuction: null,
    loading: false,
    currentAuctionLoading: false,
    placingBid: false,
    error: null,
    topBidderLoading: false
};


const deriveCollections = (auctions: CarAuction[]) => {
    const endingSoon = [...auctions]
        .sort((a, b) => new Date(a.ending).getTime() - new Date(b.ending).getTime());

    const newlyListed = [...auctions]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const noReserve = auctions.filter((auction) => toNumber(auction.startingBid) <= 0);

    const lowestMileage = [...auctions]
        .sort((a, b) => toNumber((a as unknown as {
            millage?: unknown
        }).millage, toNumber(a.mileage)) - toNumber((b as unknown as {
            millage?: unknown
        }).millage, toNumber(b.mileage)));

    const inspected = auctions.filter((auction) => Boolean(auction.verified));

    return {endingSoon, newlyListed, noReserve, lowestMileage, inspected};
};

const deriveRelatedAuctions = (items: CarAuction[], currentAuctionId?: string | null, limit = 8) => {
    if (!currentAuctionId) return items.slice(0, limit);
    return items.filter((item) => String(item.id) !== String(currentAuctionId)).slice(0, limit);
};

const syncAuctionCollections = (state: AuctionState) => {
    const derived = deriveCollections(state.auctions);
    state.endingSoon = derived.endingSoon;
    state.newlyListed = derived.newlyListed;
    state.noReserve = derived.noReserve;
    state.lowestMileage = derived.lowestMileage;
    state.inspected = derived.inspected;

    const relatedReferenceId = state.relatedReferenceId ?? state.currentAuction?.id ?? null;
    state.relatedEndingSoon = deriveRelatedAuctions(state.endingSoon, relatedReferenceId);
    state.relatedNewlyListed = deriveRelatedAuctions(state.newlyListed, relatedReferenceId);
};

async function fetchAuctionByVehicleId(vehicleId: string) {
    const response = await supabase
        .from("auctions")
        .select("*, vehicle:vehicles!inner(*, seller:dealerships(*))")
        .eq("vehicle.published", true)
        .eq("vehicle_id", vehicleId)
        .maybeSingle();

    if (response.error) {
        throw new Error(response.error.message);
    }

    if (!response.data) {
        throw new Error("Auction not found");
    }
    const bidResponse = await supabase.from('auction_bids')
        .select('*, user:profiles(*)', {count: 'exact'})
        .eq('auction_id',response.data.id)
        .order('created_at', {ascending: false})
        .limit(1)
    if (bidResponse.error){
        throw new Error("Failed to fetch top bidder");
    }

    if (!bidResponse.data || bidResponse.data.length === 0) {
        // ("Vehicle has no auctions")
        return toCarAuction(response.data);
    } else {
        return toCarAuction({
            ...response.data,
            bids: [keysToCamelCase<AuctionBid>(bidResponse.data[0])],
            totalBids: bidResponse.count ?? 0,
        });
    }


}

type PlaceBidResult = {
    auction_id: string;
    vehicle_id: string;
    current_bid: number;
};

function applyAuctionUpdate(state: AuctionState, updatedAuction: CarAuction) {
    state.currentAuction = updatedAuction;

    state.auctions = state.auctions.some((auction) => auction.id === updatedAuction.id)
        ? state.auctions.map((auction) => auction.id === updatedAuction.id ? updatedAuction : auction)
        : [...state.auctions, updatedAuction];
    syncAuctionCollections(state);
}

export const fetchAuctionsAsync = createAsyncThunk<CarAuction[], void, { rejectValue: string }>(
    "auction/fetchAuctions",
    async (_, {rejectWithValue}) => {
        try {
            const response = await supabase
                .from("auctions")
                .select("*, vehicle:vehicles!inner(*, seller:dealerships(*))")
                .eq("vehicle.published", true)
                .order("created_at", {ascending: false});

            if (response.error) {
                return rejectWithValue(response.error.message);
            }

            return (response.data ?? []).map(toCarAuction);
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue("Failed to fetch auctions");
        }
    }
);

export const fetchAuctionByIdAsync = createAsyncThunk<CarAuction, string, { rejectValue: string }>(
    "auction/fetchAuctionById",
    async (id, {rejectWithValue}) => {
        try {
            return await fetchAuctionByVehicleId(id);
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue("Failed to fetch auction");
        }
    }
);

export const setCurrentAuctionAsync = createAsyncThunk<CarAuction, string, { rejectValue: string }>(
    "auction/setAuctionById",
    async (id, {rejectWithValue, getState}) => {
        const {auction} = getState() as { auction: AuctionState };
        try {
            const item = auction.auctions.find((auction) => auction.id === id);

            if (item) return item;

            return await fetchAuctionByVehicleId(id);
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue("Failed to fetch auction");
        }
    }
);

export const refreshAuctionAsync = createAsyncThunk<CarAuction, string, { rejectValue: string }>(
    "auction/refreshAuction",
    async (vehicleId, {rejectWithValue}) => {
        try {
            return await fetchAuctionByVehicleId(vehicleId);
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to refresh auction"));
        }
    }
);

export const makeBidAsync = createAsyncThunk<
    CarAuction,
    number,
    { state: RootState; rejectValue: string }
>(
    "auction/makeBid",
    async (amount, {rejectWithValue, getState}) => {
        try {
            console.log("[makeBidAsync] started", {amount});
            const state = getState();
            const currentAuction = state.auction.currentAuction;
            const userId = state.authentication.user?.id;

            console.log("[makeBidAsync] state snapshot", {
                currentAuction,
                userId
            });

            if (!currentAuction) {
                console.log("[makeBidAsync] aborting: no auction selected");
                return rejectWithValue("No auction selected");
            }

            if (!userId) {
                console.log("[makeBidAsync] aborting: no authenticated user");
                return rejectWithValue("You must be logged in to place a bid");
            }

            const vehicleId = String(currentAuction.id);
            const auctionId = String(currentAuction.auctionId);
            const nextBidAmount = toNumber(amount);

            console.log("[makeBidAsync] resolved ids", {
                vehicleId,
                auctionId,
                nextBidAmount
            });

            if (nextBidAmount <= 0) {
                console.log("[makeBidAsync] aborting: invalid amount", {nextBidAmount});
                return rejectWithValue("Bid amount must be greater than zero");
            }

            console.log("[makeBidAsync] calling place_bid rpc", {
                auctionId,
                nextBidAmount
            });
            const bidResponse = await supabase
                .rpc("place_bid", {
                    p_auction_id: auctionId,
                    p_amount: nextBidAmount
                })
                .maybeSingle();

            console.log("[makeBidAsync] place_bid response", bidResponse);

            if (bidResponse.error) {
                console.log("[makeBidAsync] aborting: rpc error", bidResponse.error);
                return rejectWithValue(bidResponse.error.message);
            }

            const bidData = bidResponse.data as PlaceBidResult | null;
            const refreshedVehicleId = String(bidData?.vehicle_id ?? vehicleId);

            console.log("[makeBidAsync] refetching updated auction", {vehicleId: refreshedVehicleId});
            const refreshedAuction = await fetchAuctionByVehicleId(refreshedVehicleId);
            console.log("[makeBidAsync] success", refreshedAuction);
            return refreshedAuction;
        } catch (error) {
            console.log("[makeBidAsync] unexpected error", error);
            return rejectWithValue(normalizeError(error, "Failed to place bid"));
        }
    }
);

export const fetchTopBidder = createAsyncThunk(
    'auction/fetchTopBidder',
    async (auctionId:string,{rejectWithValue}) => {
        try {
            const response = await supabase.from('auction_bids')
                .select('*, user:profiles(*)', {count: 'exact'})
                .eq('auction_id',auctionId)
                .order('created_at', {ascending: false})
                .limit(1)
            if (response.error){
                return rejectWithValue("Failed to fetch top bidder");
            }

            if (!response.data || response.data.length === 0) {
                return rejectWithValue("Vehicle has no auctions")
            }
            return {
                bid: keysToCamelCase<AuctionBid>(response.data[0]),
                count: response.count ?? 0
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, 'failed to fetch top bidder'));
        }

    }
)

const auctionSlice = createSlice({
    name: "auction",
    initialState,
    reducers: {
        clearCurrentAuction: (state) => {
            state.currentAuction = null;
            state.relatedReferenceId = null;
            state.relatedEndingSoon = [];
            state.relatedNewlyListed = [];
        },
        setEndingSoon: (state, action) => {
            state.endingSoon = action.payload;
            state.relatedEndingSoon = deriveRelatedAuctions(
                state.endingSoon,
                state.relatedReferenceId ?? state.currentAuction?.id
            );
        },
        setRelatedAuctionReferenceId: (state, action: {payload: string | null}) => {
            state.relatedReferenceId = action.payload;
            state.relatedEndingSoon = deriveRelatedAuctions(state.endingSoon, action.payload);
            state.relatedNewlyListed = deriveRelatedAuctions(state.newlyListed, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAuctionsAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAuctionsAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.auctions = action.payload;
                syncAuctionCollections(state);
                state.auctionsFetched = true;
            })
            .addCase(fetchAuctionsAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? "Failed to fetch auctions";
            });
        builder.addCase(fetchAuctionByIdAsync.pending, (state) => {
            state.currentAuctionLoading = true;
            state.error = null;
        })
            .addCase(fetchAuctionByIdAsync.fulfilled, (state, action) => {
                state.currentAuctionLoading = false;
                applyAuctionUpdate(state, action.payload);
            })
            .addCase(fetchAuctionByIdAsync.rejected, (state, action) => {
                state.currentAuctionLoading = false;
                state.error = action.payload ?? "Failed to fetch auction";
            });
        builder.addCase(setCurrentAuctionAsync.pending, (state) => {
            state.currentAuctionLoading = true;
            state.error = null;
        })
            .addCase(setCurrentAuctionAsync.fulfilled, (state, action) => {
                state.currentAuctionLoading = false;
                applyAuctionUpdate(state, action.payload);
            })
            .addCase(setCurrentAuctionAsync.rejected, (state, action) => {
                state.currentAuctionLoading = false;
                state.error = action.payload ?? "Failed to fetch auction";
            })
            .addCase(refreshAuctionAsync.fulfilled, (state, action) => {
                applyAuctionUpdate(state, action.payload);
            })
            .addCase(refreshAuctionAsync.rejected, (state, action) => {
                state.error = action.payload ?? state.error;
            })
            .addCase(makeBidAsync.pending, (state) => {
                state.placingBid = true;
                state.error = null;
            })
            .addCase(makeBidAsync.fulfilled, (state, action) => {
                state.placingBid = false;
                applyAuctionUpdate(state, action.payload);
            })
            .addCase(makeBidAsync.rejected, (state, action) => {
                state.placingBid = false;
                state.error = action.payload ?? "Failed to place bid";
            });
        builder
            .addCase(fetchTopBidder.pending, (state) => {
                state.topBidderLoading = true
            })
            .addCase(fetchTopBidder.fulfilled, (state, action) => {

                if (!state.currentAuction) return
                console.log(action.payload);
                const updatedAuction = {
                    ...state.currentAuction,
                    currentBid: action.payload.bid.amount,
                    bids: [action.payload.bid, ...(state.currentAuction.bids ?? [])],
                    totalBids: action.payload.count
                };
                applyAuctionUpdate(state, updatedAuction);
                state.topBidderLoading = false
            })
            .addCase(fetchTopBidder.rejected, (state, action) => {
                state.error = action.payload as string ?? "Failed to fetch top bid";
                state.topBidderLoading = false
            })
    }
});

export const {clearCurrentAuction, setEndingSoon, setRelatedAuctionReferenceId} = auctionSlice.actions;
export default auctionSlice.reducer;
